const Environment = require('./Environment');
const ContentDefault = require('../constants/Content');
const Logger = require('./Logger');
const serviceUtils = require('./serviceUtils');

const Content = ContentDefault.default;

const addUserProfile = (userData) => {
  try {
    const _learnq = window._learnq || [];

    const today = new Date();
    let points = 0;
    let expiredPoints = 0;
    let usedPoints = 0;
    let unexpiredPoints = 0;
    if (userData.points) {
      userData.points.forEach((pointData) => {
        if (pointData.pointsDifference < 0) {
          usedPoints += pointData.pointsDifference;
        } else if (pointData.expirationDate < today) {
          expiredPoints += pointData.pointsDifference;
        } else {
          unexpiredPoints += pointData.pointsDifference;
        }
      });
      if (expiredPoints + usedPoints >= 0) points = unexpiredPoints;
      else points = unexpiredPoints + expiredPoints + usedPoints;
    }
    if (userData && (userData.email || userData.guestCheckoutEmail)) {
      // Identifying a person and tracking special Klaviyo properties.
      _learnq.push([
        'identify',
        {
          $email: userData.email || userData.guestCheckoutEmail,
          $first_name: userData.name ? userData.name.first : '', // eslint-disable-line camelcase
          $last_name: userData.name ? userData.name.last : '', // eslint-disable-line camelcase
          IsSubscribedToNewsletter: userData.isSubscribedToNewsletter,
          Points: points,
        },
      ]);
    }
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const updateUserPoints = (points) => {
  try {
    const _learnq = window._learnq || [];

    _learnq.push([
      'identify',
      {
        Points: points,
      },
    ]);
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const identifyUser = (userInfo) => {
  try {
    const _learnq = window._learnq || [];

    if (userInfo && userInfo.email) {
      _learnq.push([
        'identify',
        {
          $email: userInfo.email,
          $source: userInfo.source,
        },
      ]);

      _learnq.push([
        'track',
        'Created',
        {
          url: window.location.href,
        },
      ]);
    }

    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const trackPersonalizedProduct = (subscription) => {
  try {
    const _learnq = window._learnq || [];

    const personalizedData = {
      Plan: `${Content.names.shortName} ${subscription.plan.title} Subscription`, // Bitsy Month to Month, Bitsy Three Month, Bitsy Six Month
      PlanCost:
        subscription.plan.costWithCoupon || subscription.plan.actualCost,
      UrlToContinue: `${Environment.getSelfDomain}/checkout/shipping-and-payment`,
      BabyGender: subscription.babyGender,
      BabyBirthday: subscription.babyBirthday
        ? new Date(subscription.babyBirthday)
        : '',
      PersonalizedOn: new Date(),
    };
    _learnq.push(['track', 'Personalized Product', personalizedData]);
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const viewedProduct = (product) => {
  try {
    const _learnq = window._learnq || [];

    const mediaUrl = serviceUtils.getProductImageByColor(product.media, 'NONE');
    const viewedProductData = {
      SKU: product.sku,
      ProductName: product.name,
      ItemPrice: product.actualCost,
      ProductURL: `${Environment.getSelfDomain}/products/${product.urlName}`,
      ImageURL: mediaUrl,
      ID: product._id,
      ViewedOn: new Date(),
    };
    _learnq.push(['track', 'Viewed Product', viewedProductData]);
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const viewedSubscription = (plan) => {
  try {
    const _learnq = window._learnq || [];

    _learnq.push([
      'track',
      'Viewed Subscription',
      {
        Plan: `${Content.names.shortName} ${plan.title} Subscription`,
        $value: plan.costWithCoupon || plan.actualCost,
        ViewedOn: new Date(),
      },
    ]);
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const determineSubscriptionField = (cart, cartData) => {
  const cartDataObj = cartData;
  if (cart.subscription.plan.title === 'Month to Month') {
    cartDataObj.hasMontlySubscription = true;
  } else if (cart.subscription.plan.title === 'Six Month') {
    cartDataObj.has6MonthSubscription = true;
  } else if (cart.subscription.plan.title === 'Nine Month') {
    cartDataObj.has9MonthSubscription = true;
  } else if (cart.subscription.plan.title === 'Twelve Month') {
    cartDataObj.has12MonthSubscription = true;
  }
  return cartDataObj;
};

const updatedCart = (cart) => {
  try {
    const _learnq = window._learnq || [];
    const includeViewedOn = true;
    const items = serviceUtils.getDataForCart(cart, includeViewedOn);
    if (items && items.length) {
      let cartData = {
        $value: cart.subCosts ? cart.subCosts.overallTotal : 0,
        Items: items,
        UpdatedOn: new Date(),
        Provider: Environment.store,
        hasSubscription: !!cart.subscription,
      };

      if (cart.subscription) {
        cartData = determineSubscriptionField(cart, cartData);
      }

      _learnq.push(['track', 'Updated Cart', cartData]);
      return _learnq;
    }
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const setDueDateForUser = (date, email) => {
  try {
    const _learnq = window._learnq || [];
    let dateObject;
    if (typeof date !== 'object') {
      dateObject = new Date(date);
    } else {
      dateObject = date;
    }

    if (typeof dateObject === 'object' && email) {
      _learnq.push([
        'identify',
        {
          $email: email,
          DueDate: dateObject,
        },
      ]);
    }

    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const checkoutStarted = (cart) => {
  try {
    const _learnq = window._learnq || [];
    const includeViewedOn = true;

    const items = serviceUtils.getDataForCart(cart, includeViewedOn);
    if (items && items.length) {
      let cartData = {
        $value: cart.subCosts ? cart.subCosts.overallTotal : 0,
        Items: items,
        StartedAt: new Date(),
        Provider: Environment.store,
        hasSubscription: !!cart.subscription,
      };

      if (cart.subscription) {
        cartData = determineSubscriptionField(cart, cartData);
      }

      _learnq.push(['track', 'Checkout Started', cartData]);
      return _learnq;
    }
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const viewPartner = (email, partner) => {
  try {
    const _learnq = window._learnq || [];

    if (partner && email) {
      _learnq.push([
        'identify',
        {
          $email: email,
          viewedBPPartner: true,
          BreastPumpStep: 4,
        },
      ]);
    }

    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const addRegistryToUser = (email, dueDate) => {
  try {
    const _learnq = window._learnq || [];

    if (email) {
      _learnq.push([
        'identify',
        {
          $email: email,
          hasRegistry: true,
          DueDate: dueDate,
        },
      ]);
    }

    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const breastPumpProcess = (stepNum, email, bpData) => {
  try {
    const _learnq = window._learnq || [];

    if (email) {
      if (stepNum === 1) {
        _learnq.push([
          'identify',
          {
            $email: email,
            BreastPumpStep: stepNum,
            firstName: bpData.firstName,
            lastName: bpData.lastName,
            phoneNumber: bpData.phoneNumber,
            DueDate: bpData.dueDate,
            IsSubscribedToNewsletter: bpData.isSubscribedToNewsletter,
          },
        ]);
      }
      if (stepNum === 2) {
        _learnq.push([
          'identify',
          {
            $email: email,
            BreastPumpStep: stepNum,
            city: bpData.city,
            state: bpData.state,
            country: bpData.country,
            zipCode: bpData.zipCode,
          },
        ]);
      }
      if (stepNum === 3) {
        _learnq.push([
          'identify',
          {
            $email: email,
            BreastPumpStep: stepNum,
            insuranceType: bpData.insuranceType,
            BreastPumpPartner: bpData.matchedPartner,
          },
        ]);
      }
    }

    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const breastPumpPartnerApiInfoSent = (email, partner) => {
  try {
    const _learnq = window._learnq || [];

    if (partner && email) {
      _learnq.push([
        'identify',
        {
          $email: email,
          BPPartnerApiSent: true,
          BreastPumpStep: 4,
        },
      ]);
    }

    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

const registryProcess = (stepNum, email, registryCode) => {
  try {
    const _learnq = window._learnq || [];

    if (email) {
      _learnq.push([
        'identify',
        {
          $email: email,
          RegistryStep: stepNum,
          Registry: registryCode,
        },
      ]);
    }
    return _learnq;
  } catch (e) {
    Logger.error(e);
    return e;
  }
};

module.exports = {
  addUserProfile,
  identifyUser,
  trackPersonalizedProduct,
  viewedProduct,
  viewedSubscription,
  updatedCart,
  updateUserPoints,
  setDueDateForUser,
  checkoutStarted,
  viewPartner,
  addRegistryToUser,
  breastPumpProcess,
  breastPumpPartnerApiInfoSent,
  registryProcess,
};
